import { AssistClient } from "@/lib/clients/assistClient";
import { NovelClient } from "@/lib/clients/novelClient";
import { Assist, AssistFunctionType } from "@/lib/models/assist";

const HTML_FUNCTION_TYPES: AssistFunctionType[] = [
  AssistFunctionType.MATCHING_COMPANY,
  AssistFunctionType.GENERATE_CATCHCOPY,
  AssistFunctionType.MATCHING_THEME,
  AssistFunctionType.GENERATE_THEME,
];

export function isAssistHtmlResult(functionType: AssistFunctionType | string): boolean {
  return HTML_FUNCTION_TYPES.includes(functionType as AssistFunctionType);
}

export function assistResultText(assist: Assist): string {
  const message = assist.result?.message?.trim() || "";
  const html = assist.result?.html?.trim() || "";
  if (isAssistHtmlResult(assist.functionType) && html) {
    return html;
  }
  return message || html;
}

export function assistResultPreview(assist: Assist, maxLength = 120): string {
  const raw = assistResultText(assist);
  const text = raw.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (!text) {
    return "";
  }
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

export function assistResultCreatedAt(assist: Assist): Date {
  const parsed = new Date(assist.createdAt);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

export interface LatestAssistLog {
  assist: Assist;
  novelTitle: string;
}

/** 既存の novels + listAssistLog から、全作品を通した最新 1 件を返す */
export async function fetchLatestAssistLog(
  novelClient: NovelClient = new NovelClient(),
  assistClient: AssistClient = new AssistClient()
): Promise<LatestAssistLog | null> {
  const novels = await novelClient.fetchAllNovel();
  if (!novels.length) {
    return null;
  }

  const logsPerNovel = await Promise.all(
    novels.map(async (novel) => {
      try {
        const logs = await assistClient.listAssistLog({ novelId: novel.novelId });
        const assist = logs[0];
        if (!assist) {
          return null;
        }
        return { assist, novelTitle: novel.title?.trim() || "" };
      } catch {
        return null;
      }
    })
  );

  return (
    logsPerNovel
      .filter((log): log is LatestAssistLog => !!log)
      .sort((a, b) => assistResultCreatedAt(b.assist).getTime() - assistResultCreatedAt(a.assist).getTime())[0] || null
  );
}
